// The marks on the runs list: what the self-audit found, and whether AxisCare
// has changed since the run was built.
//
// Each check costs an AxisCare fetch and a handful of reads, so the list does
// not check all 37 runs. It checks the selected run first, then the six most
// recent unpaid ones, one at a time in the background; any other run is
// checked when it is opened. Results are kept for the browser session and
// keyed to the run's totals and status, so a run that changes is checked again.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { auditRun } from '../../../lib/billingAudit';
import { loadRunLines } from '../../../lib/billing/runs';
import { classifyResync } from '../../../lib/billing/resync';
import { clientOf } from '../../../lib/billing/claimLines';
import { visitsFromResponse } from '../../../lib/axiscareVisits';
import { destinationForPayer } from '../../../lib/serviceModel';
import { loadAuditBase, loadAuditVisits } from './auditInputs';
import { billableVisits, buildClientMap, fetchClientsForMatching } from './visitData';

const CACHE_KEY = 'caera.runMarks.v1';
const BACKGROUND = 6;
// Runs before August 2026 were built on the old footing (clocked hours, 1.5x),
// so comparing them with today's rules would flag nearly every line.
const COMPARABLE_FROM = '2026-08-01';

/** What makes a cached mark stale: anything that changes when the run does. */
export const runSignature = (run) =>
  [run.total_lines, run.total_accrued_amount, run.total_paid_amount, run.status, run.date_from, run.date_to].join('|');

const readCache = () => {
  try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); } catch { return {}; }
};
const writeCache = (marks) => {
  try {
    const done = Object.fromEntries(Object.entries(marks).filter(([, m]) => m.state === 'done'));
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(done));
  } catch { /* storage unavailable: marks are simply re-checked next time */ }
};

/** Check one run. `clients` is the client list for matching, loaded once per batch. */
export const checkRun = async (run, clients) => {
  const lines = await loadRunLines(supabase, run.id);
  if (!lines.length) return { serious: 0, review: 0, changed: null, newVisits: null, changedLineIds: [] };
  const [base, vis] = await Promise.all([loadAuditBase(run, lines), loadAuditVisits(run)]);
  if (base.error) throw new Error(base.error);
  // Exactly what the Self-audit tab passes, so the mark and the tab agree.
  const audit = auditRun({ run, lines, ...base, visits: !vis.error && Array.isArray(vis.visits) ? vis.visits : null, staff: vis.staff || [] });

  let changed = null; let newVisits = null; let changedLineIds = [];
  if (!vis.error && run.date_from >= COMPARABLE_FROM) {
    // The same comparison resync makes: on the run's own basis and destination.
    const basis = run.hours_basis || 'scheduled';
    const billable = billableVisits(visitsFromResponse(vis.visits), basis);
    const cMap = await buildClientMap(billable, clients);
    const mine = billable.filter(v => destinationForPayer(clientOf(v, cMap).payer) === (run.payer || 'gammis'));
    const classified = classifyResync({ visits: mine, existingLines: lines, basis });
    changedLineIds = classified.filter(c => c.status === 'updated').map(c => c.existingLine.id);
    changed = changedLineIds.length;
    newVisits = classified.filter(c => c.status === 'new').length;
  }
  return { serious: audit.serious, review: audit.review, changed, newVisits, changedLineIds };
};

export default function useRunMarks(runs, selected) {
  const [marks, setMarks] = useState(readCache);
  const marksRef = useRef(marks);
  const queue = useRef([]);
  const busy = useRef(false);
  const clients = useRef(null);

  const update = useCallback((id, mark) => {
    setMarks(prev => {
      const next = { ...prev, [id]: mark };
      marksRef.current = next;
      writeCache(next);
      return next;
    });
  }, []);

  const drain = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      while (queue.current.length) {
        const run = queue.current.shift();
        const sig = runSignature(run);
        const have = marksRef.current[run.id];
        if (have && have.sig === sig && have.state === 'done') continue;
        update(run.id, { state: 'checking', sig });
        try {
          if (!clients.current) clients.current = await fetchClientsForMatching();
          update(run.id, { state: 'done', sig, ...(await checkRun(run, clients.current)) });
        } catch (err) {
          update(run.id, { state: 'error', sig, message: err.message });
        }
      }
    } finally {
      busy.current = false;
    }
  }, [update]);

  const enqueue = useCallback((list, { first = false } = {}) => {
    const ids = new Set(queue.current.map(r => r.id));
    const fresh = list.filter(r => r && !ids.has(r.id));
    queue.current = first ? [...fresh, ...queue.current.filter(r => !fresh.some(f => f.id === r.id))] : [...queue.current, ...fresh];
    drain();
  }, [drain]);

  // The selected run first, then the most recent unpaid ones.
  useEffect(() => {
    if (!runs.length) return;
    const recent = runs.filter(r => r.status !== 'closed').slice(0, BACKGROUND);
    enqueue(selected ? [selected, ...recent] : recent);
  }, [runs, selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Check a run again now — after something on it changed that its totals may not show. */
  const recheck = useCallback((run) => {
    if (!run) return;
    const next = { ...marksRef.current };
    delete next[run.id];
    marksRef.current = next;
    setMarks(next);
    enqueue([run], { first: true });
  }, [enqueue]);

  return { marks, recheck };
}
